import { type Option } from "@tento-lona/sheets";
import { RowLocalId } from "@tento-lona/sheets";
import { SheetViewModel } from "@tento-lona";
import { Tracing } from "@tento-core/tracing";
import { Point } from "@tento-core/point";
import { rowIdentity } from "@tento-ui/components/sheet/row-identity";
import { SheetCell } from "@tento-ui/components/sheet/sheet-cell";
import { SheetLayoutPort } from "@tento-ui/components/sheet/sheet-layout";
import { SheetDelegate } from "./sheet-delegate.ts";
import { SheetBinder } from "./sheet-index-renderer.ts";

/**
 * The sheet element's static-child handle (F11): place, update, look up and
 * dismiss static children (span overflow content anchored to a row) through
 * the layout's static-children surface. Moved out of sheet-element.ts
 * unchanged; the element hands in its sheet, layout children and static-child
 * y position, read when a method runs.
 */

/** What the handle reads from its sheet element (read when a method runs). */
export type StaticChildHandleHost = {
  /** The bound sheet, if any. */
  readonly sheet: Option<SheetViewModel.Surface<SheetBinder>>;
  /** The layout's children surfaces. */
  readonly children: SheetLayoutPort["children"];
  /** The element's static-child y position for a row. */
  readonly staticChildYPosition: (rowId: RowLocalId, rowLayout: SheetCell.Layout) => Option<{ px: number; anchor: "top" | "bottom" }>;
};

/** The handle the element exposes as `staticChildHandle`. */
export function makeStaticChildHandle(host: StaticChildHandleHost): SheetDelegate.StaticChildHandle {
  return {
      surface(rowId: RowLocalId): Option<SheetDelegate.SurfaceLocation> {
        return host.sheet?.joinedTree.getRowLocation(rowId) ?? null;
      },
      reveal(
        id: string,
        $e: HTMLElement,
        rowId: RowLocalId,
        options: Option<{
          fillWidth?: boolean;
          align?: "center";
          offset?: Point;
        }>,
      ) {
        const dbg = id === "calendar-month";
        if (!host.sheet) {
          if (dbg) Tracing.log("dbg/static-reveal", { id, bail: "no-sheet" });
          return;
        }

        const rowLayout = host.sheet.layout.getRowLayout(rowId);
        if (!rowLayout) {
          if (dbg) {
            Tracing.log("dbg/static-reveal", {
              id,
              rowId: rowId.toString(),
              bail: "no-rowLayout",
            });
          }
          return;
        }

        const yPos = host.staticChildYPosition(rowId, rowLayout);
        if (yPos == null) {
          if (dbg) {
            Tracing.log("dbg/static-reveal", {
              id,
              rowId: rowId.toString(),
              bail: "no-yPos",
            });
          }
          return;
        }

        const location = host.sheet.joinedTree.getRowLocation(rowId);
        if (!location) {
          if (dbg) {
            Tracing.log("dbg/static-reveal", {
              id,
              rowId: rowId.toString(),
              bail: "no-location",
            });
          }
          return;
        }

        let xPx = (options?.offset?.x ?? 0) + "px";
        if (options?.align) {
          $e.style.left = "50%";
          switch (options.align) {
            case "center": {
              xPx = "-50%";
            }
          }
        } else {
          $e.style.left = "0px";
        }

        if (options?.offset) {
          // Offset is relative to the row's static child (e.g., calendar element).
          $e.style.top = "auto";
          $e.style.bottom = "auto";
          $e.style.top = `${yPos.px + options.offset.y}px`;
          $e.style.left = `${options.offset.x}px`;
        } else if (yPos.anchor === "bottom") {
          $e.style.top = "auto";
          $e.style.bottom = `${yPos.px}px`;
          $e.style.transform = `translateX(${xPx})`;
        } else {
          $e.style.bottom = "auto";
          $e.style.top = `${yPos.px}px`;
          $e.style.transform = `translateX(${xPx})`;
        }

        if (dbg) {
          Tracing.log("dbg/static-reveal", {
            id,
            rowId: rowId.toString(),
            bail: "revealed",
            yPx: yPos.px,
            location,
          });
        }
        host.children.static.reveal(
          id,
          $e,
          rowIdentity(rowId.toString()),
          location,
          options?.fillWidth ?? false,
        );
      },
      updateOffset(id: string, rowId: RowLocalId, offset: Point, motion: SheetDelegate.Motion) {
        if (!host.sheet) return;
        const $e = this.get(id, rowId);
        if (!$e) return;

        const rowLayout = host.sheet.layout.getRowLayout(rowId);
        if (!rowLayout) return;
        const yPos = host.staticChildYPosition(rowId, rowLayout);
        if (!yPos) return;

        if (motion === "animated") {
          $e.style.transition = "top 0.3s ease, left 0.3s ease";
        } else {
          $e.style.transition = "none";
        }
        $e.style.top = `${yPos.px + offset.y}px`;
        $e.style.left = `${offset.x}px`;
      },
      get(id: string, rowId: RowLocalId): Option<HTMLElement> {
        if (!host.sheet) return null;
        const location = host.sheet.joinedTree.getRowLocation(rowId);
        if (!location) return null;
        return host.children.static.get(
          id,
          rowIdentity(rowId.toString()),
          location,
        ) ?? null;
      },
      dismiss(id: string, rowId: RowLocalId) {
        if (!host.sheet) return null;
        const location = host.sheet.joinedTree.getRowLocation(rowId);
        if (!location) return null;
        host.children.static.dismiss(
          id,
          rowIdentity(rowId.toString()),
          location,
        );
      },
  };
}
